import { AuthContext } from "@/iti-core-react/contexts/auth";
import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { ItiMessageService } from "@/iti-core-react/services/iti-message-service";
import { ConditionClauseResourceDto } from "@/shared/dtos/Centralized/condition-clause-resource-dto";
import { ConditionDto } from "@/shared/dtos/Centralized/condition-dto";
import { UserStatus } from "@/shared/enums/user-status";
import { UserRegistrationConditionService } from "@/shared/services/userRegistrationConditionService";
import { UserService } from "@/shared/services/userService";
import { useCallback, useContext, useRef, useState } from "react";

/*
 * Encapsulación de la lógica de autenticación del usuario.
 */
export function useAuth() {
	const { signIn, signOut, setProfile } = useContext(AuthContext);
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [conditionClauses, setConditionClauses] = useState(new ConditionDto());
	const [legalConditionsPopupVisible, setLegalConditionsPopupVisible] =
		useState(false);
	const [conditionClauseId, setConditionClauseId] = useState<any>(null);
	const [user, setUser] = useState<any>(null);
	const formData = useRef({});
	const { t } = useLanguage();

	// Resetear el estado cuando se cierre sesión
	const resetAuthState = useCallback(() => {
		setIsAuthenticated(false);
		setLegalConditionsPopupVisible(false);
		setUser(null);
	}, []);

	// Verificación del estado del usuario
	const checkUserStatus = useCallback(async () => {
		try {
			const user = await UserService.getCurrentUser();
			if (!user) {
				signOut?.();
				return;
			}

			const userStatus = user.userStatus;

			if (userStatus === UserStatus.REGISTERED) {
				setLegalConditionsPopupVisible(true);
				setUser(user);
				setProfile?.(user);
			} else if (userStatus === UserStatus.ACTIVE) {
				setIsAuthenticated(true);
				if (setProfile) {
					setProfile(user);
				}
			} else {
				signOut?.();
			}
		} catch (error) {
			signOut?.();
		}
	}, [signOut, setProfile]);

	// Autenticación del usuario
	const authenticateUser = useCallback(async () => {
		const { userName, password, email }: any = formData.current;

		const credentials = {
			UserName: userName,
			Pwd: password,
			email: email,
		};

		if (signIn) {
			const loginSuccess = await signIn(credentials);
			if (loginSuccess) {
				await checkUserStatus();
			} else {
				ItiMessageService.toastError(t("login.error"), {
					cssClass: "toast-login-error",
				});
			}
			return loginSuccess;
		}
		return false;
	}, [signIn, checkUserStatus]);

	const validateUser = useCallback(async () => {
		return await UserService.validateCurrentUser();
	}, []);

	// Creación de recurso de cláusula y condición de uso para el usuario
	const createConditionClauseResourceUser = useCallback(
		async (conditionClauseResource: ConditionClauseResourceDto) => {
			const language = localStorage.getItem("language") || "es";
			setConditionClauseId(conditionClauseResource.conditionClauseId);

			return await UserRegistrationConditionService.createConditionClauseUser([
				new ConditionClauseResourceDto({
					conditionClauseId: conditionClauseResource.conditionClauseId,
					conditionClauseLang: language,
				}),
			]);
		},
		[],
	);

	return {
		isAuthenticated,
		legalConditionsPopupVisible,
		conditionClauses,
		formData,
		conditionClauseId,
		authenticateUser,
		validateUser,
		createConditionClauseResourceUser,
		setLegalConditionsPopupVisible,
		setConditionClauses,
		setIsAuthenticated,
		user,
		setProfile,
		checkUserStatus,
		resetAuthState,
	};
}
